import { memo, useCallback, useEffect, useRef } from 'react';

import { useForm } from 'react-hook-form';
import { FiSend } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

import { Avatar } from '@components';
import { useAuth, useCreateCommentMutation } from '@hooks';
import { selectUser } from '@hooks/useAuth/selectors';

import EmojiPickerPopover from '../../../EmojiPickerPopover';
import * as S from './styles';

type FormField = {
  comment: string;
};

type NewCommentInputProps = {
  postId: number;
};

const NewCommentInput = ({ postId }: NewCommentInputProps) => {
  const user = useAuth(selectUser);

  const { handleSubmit, reset, register, setValue, getValues } =
    useForm<FormField>();

  const { mutate } = useCreateCommentMutation(postId);

  const inputRef = useRef<HTMLTextAreaElement>();

  const { ref, ...inputRegister } = register('comment', {
    required: true,
  });
  const { t } = useTranslation();

  const handleRef = (node: HTMLTextAreaElement | null) => {
    if (node) {
      ref(node);
      inputRef.current = node;
    }
  };

  const onSubmit = handleSubmit(({ comment }) => {
    mutate(comment);
    reset({ comment: '' });
  });

  const handleEmojiSelect = useCallback(
    (emoji: string) => {
      const textarea = inputRef.current;

      if (!textarea) return;

      const { selectionStart, selectionEnd } = textarea;
      const currentValue = getValues('comment') || '';
      const newValue =
        currentValue.slice(0, selectionStart) +
        emoji +
        currentValue.slice(selectionEnd);

      setValue('comment', newValue, { shouldValidate: true });

      requestAnimationFrame(() => {
        const pos = selectionStart + emoji.length;
        textarea.setSelectionRange(pos, pos);
        textarea.focus();
      });
    },
    [getValues, setValue],
  );

  useEffect(() => {
    const handleEnterKeyPress = (e: HTMLElementEventMap['keydown']) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSubmit();
      }
    };

    inputRef.current?.addEventListener('keydown', handleEnterKeyPress);

    return () => {
      inputRef.current?.removeEventListener('keydown', handleEnterKeyPress);
    };
  }, [onSubmit]);

  return (
    <S.Container>
      <Avatar src={user.avatar} alt={user.name} />

      <S.InputBox>
        <S.AutosizeInput
          placeholder={t('pages.newsfeed.newComment')}
          maxRows={3}
          {...inputRegister}
          ref={handleRef}
        />
        <EmojiPickerPopover onEmojiSelect={handleEmojiSelect} size="small" />
        <S.SendButton type="button" onClick={onSubmit} title="Enviar">
          <FiSend size="0.875rem" />
        </S.SendButton>
      </S.InputBox>
    </S.Container>
  );
};

export default memo(NewCommentInput);
